/**
 * The one host router for what an HTML frame page asks of the app.
 *
 * Today it serves the `action` namespace: allow-listed buttons
 * (`html-frame-actions.ts`). An action runs only on a gesture the app saw:
 * its own overlay click, or (inside the page, where page script could forge
 * the report) after the user confirms. Nothing the page's window sends proves
 * what the user chose, so every action that writes or wakes an agent waits
 * for the app's own confirmation. The app never posts a result back.
 *
 * `denker.data` / `denker.state` join here as `data.*` / `state.*` when they
 * ship; the runtime then opens a MessageChannel port after a `bridge.hello`
 * handshake, action messages move onto that port, and this listener on the
 * window goes. Confirmation of state-changing actions stays.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { translate } from "@/i18n";
import { framesHtmlMessages } from "@/i18n/messages/frames-html";
import { isPinSurface } from "@/desktop/pin/desktop-pin-surface";
import { openDesktopPinInFocus } from "@/desktop/pin/desktop-pin-actions";
import { useNotificationStore } from "@/stores/notification-store";
import {
  needsConfirmation,
  type HtmlFrameActionHost,
  type HtmlFrameActionMode,
  type HtmlFrameHostAction,
} from "./html-frame-actions";
import {
  pendingHtmlFrameQuestion,
  runHtmlFrameHostAction,
} from "./html-frame-action-dispatch";
import {
  isHtmlFrameMessage,
  readHtmlFrameActionMessage,
} from "./html-frame-bridge";

const REPEAT_MS = 1000;
const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 10;
/** Two cancels of the page's own requests mute the page this long. */
const CANCELS_TO_MUTE = 2;
const MUTE_MS = 30_000;

export type HtmlFrameActionVerdict =
  | "ok"
  | "busy"
  | "repeat"
  | "limited"
  | "muted";

export interface HtmlFrameActionGate {
  admit(key: string, now: number, fromPage?: boolean): HtmlFrameActionVerdict;
  release(): void;
  /** The user declined the admitted request; it does not count. */
  cancel(now: number, fromPage: boolean): void;
}

/**
 * Per frame: one action in flight, no repeat within 1 s, 10 a minute. A page
 * whose own requests the user keeps declining is muted for a while, so it
 * cannot keep raising dialogs; the app's overlay clicks still pass.
 */
export function createHtmlFrameActionGate(): HtmlFrameActionGate {
  let busy = false;
  let last: { key: string; at: number } | null = null;
  let recent: number[] = [];
  let cancels: number[] = [];
  let mutedUntil = 0;
  return {
    admit(key, now, fromPage = false) {
      if (fromPage && now < mutedUntil) return "muted";
      if (busy) return "busy";
      if (last?.key === key && now - last.at < REPEAT_MS) return "repeat";
      recent = recent.filter((at) => now - at < WINDOW_MS);
      if (recent.length >= MAX_PER_WINDOW) return "limited";
      recent.push(now);
      last = { key, at: now };
      busy = true;
      return "ok";
    },
    release() {
      busy = false;
    },
    cancel(now, fromPage) {
      busy = false;
      recent.pop();
      if (!fromPage) return;
      cancels = [...cancels.filter((at) => now - at < WINDOW_MS), now];
      if (cancels.length < CANCELS_TO_MUTE) return;
      mutedUntil = now + MUTE_MS;
      cancels = [];
    },
  };
}

export interface HtmlFrameActionConfirmation {
  host: HtmlFrameActionHost;
  action: HtmlFrameHostAction;
  confirm: () => void;
  cancel: () => void;
}

/** False for an answer to a question not pending on this very frame. */
function inScope(host: HtmlFrameActionHost, action: HtmlFrameHostAction) {
  return (
    action.name !== "answer_question" ||
    pendingHtmlFrameQuestion(
      host.frameId,
      action.args.questionId,
      action.args.optionId,
    ) !== null
  );
}

export function useHtmlFrameCapabilities({
  host,
  getIframe,
  interactive,
}: {
  /** `null` while the frame has no project to act in. */
  host: HtmlFrameActionHost | null;
  getIframe: () => HTMLIFrameElement | null | undefined;
  /** The user entered the page; it may report clicks itself. */
  interactive: boolean;
}): {
  runFromOverlay: (action: HtmlFrameHostAction) => void;
  confirmation: HtmlFrameActionConfirmation | null;
} {
  const gate = useRef(createHtmlFrameActionGate()).current;
  const hostRef = useRef(host);
  hostRef.current = host;
  const [pending, setPending] = useState<{
    host: HtmlFrameActionHost;
    action: HtmlFrameHostAction;
    fromPage: boolean;
  } | null>(null);

  const execute = useCallback(
    async (target: HtmlFrameActionHost, action: HtmlFrameHostAction) => {
      try {
        // A desktop widget opens its frame in the app instead of acting.
        if (isPinSurface()) openDesktopPinInFocus(target.frameId);
        else await runHtmlFrameHostAction(target, action);
      } catch {
        useNotificationStore.getState().addSystemToast({
          id: `html-frame-action-failed-${target.frameId}`,
          title: translate(framesHtmlMessages, "actionFailed"),
          level: "error",
        });
      } finally {
        gate.release();
      }
    },
    [gate],
  );

  const request = useCallback(
    (action: HtmlFrameHostAction, mode: HtmlFrameActionMode) => {
      const target = hostRef.current;
      if (!target || !inScope(target, action)) return;
      const fromPage = mode === "interactive";
      const verdict = gate.admit(JSON.stringify(action), Date.now(), fromPage);
      if (verdict === "limited")
        useNotificationStore.getState().addSystemToast({
          id: `html-frame-action-limit-${target.frameId}`,
          title: translate(framesHtmlMessages, "actionRateLimited"),
          description: translate(
            framesHtmlMessages,
            "actionRateLimitedDescription",
          ),
          level: "warning",
        });
      if (verdict !== "ok") return;
      if (!isPinSurface() && needsConfirmation(action, mode))
        setPending({ host: target, action, fromPage });
      else void execute(target, action);
    },
    [execute, gate],
  );

  // Inside the page the frame reports clicks itself. A pin acts only on its
  // own overlay clicks.
  const listens = interactive && !isPinSurface();
  useEffect(() => {
    if (!listens) return;
    const onMessage = (event: MessageEvent) => {
      if (!isHtmlFrameMessage(event, getIframe())) return;
      const action = readHtmlFrameActionMessage(event.data);
      if (action) request(action, "interactive");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [getIframe, listens, request]);

  const runFromOverlay = useCallback(
    (action: HtmlFrameHostAction) => request(action, "overlay"),
    [request],
  );

  const confirmation = pending && {
    ...pending,
    confirm: () => {
      setPending(null);
      void execute(pending.host, pending.action);
    },
    cancel: () => {
      setPending(null);
      gate.cancel(Date.now(), pending.fromPage);
    },
  };
  return { runFromOverlay, confirmation };
}
